import { useEffect, useState, useRef } from "react";
import { Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useChatPlusStore } from "../store/chatPlusStore";
import * as api from "../services/api";
import { toast } from "sonner";

export function ChatArea() {
  const {
    messages,
    activeConversationId,
    isSending,
    setMessages,
    addMessage,
    setIsSending,
  } = useChatPlusStore();

  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeConversationId) {
      loadMessages();
    } else {
      setMessages([]);
    }
  }, [activeConversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadMessages = async () => {
    if (!activeConversationId) return;

    try {
      const data = await api.fetchMessages(activeConversationId);
      setMessages(data);
    } catch (error) {
      console.error("Erro ao carregar mensagens:", error);
      toast.error("Erro ao carregar mensagens");
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !activeConversationId || isSending) return;

    const question = input.trim();
    setInput("");

    try {
      setIsSending(true);

      // Adicionar mensagem do usuário imediatamente
      const tempUserMessage = {
        id: `temp-${Date.now()}`,
        conversation_id: activeConversationId,
        role: 'user' as const,
        content: question,
        citations: [],
        created_at: new Date().toISOString(),
      };
      addMessage(tempUserMessage);

      // Enviar pergunta
      const response = await api.askQuestion(activeConversationId, question);

      // Recarregar mensagens para pegar as versões salvas
      await loadMessages();

      toast.success("Resposta recebida");
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      toast.error("Erro ao enviar mensagem");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!activeConversationId) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p>Selecione uma conversa para começar</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Mensagens */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[70%] rounded-lg p-3 ${
                message.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              
              {message.citations && message.citations.length > 0 && (
                <div className="mt-2 pt-2 border-t border-border/50">
                  <p className="text-xs font-semibold mb-1">Citações:</p>
                  {message.citations.map((citation, idx) => (
                    <p key={idx} className="text-xs opacity-80">
                      • {citation.source} {citation.page && `(pág. ${citation.page})`}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {isSending && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-lg p-3">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-4">
        <div className="flex gap-2">
          <Textarea
            placeholder="Digite sua pergunta..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSending}
            className="resize-none"
            rows={2}
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || isSending}
            size="icon"
          >
            {isSending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
